import React, { useState, useEffect, useMemo, useRef, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import {
  getAdminBookings,
  getAllProviders,
  getProviderDetail,
  updateBookingProvider,
  updateBookingConfirmation,
  getBookingSchedule,
  getBookingDetail,
  getProvidersAvailabilityBatch,
  bulkBookingAction,
} from "./adminService";

import { ProviderModal } from "./SubComponent/ProviderModal";
import ConfirmModal from "./SubComponent/ConfirmModal";
import BookingDetailModal from "./SubComponent/BookingDetailModal";
import "./ManagerScreen.css";

/* ── Inline SVG icon set (Lucide-style, 1.75 stroke, currentColor) ── */
const Icon = ({ d, size = 16, fill = "none", stroke = "currentColor", strokeWidth = 1.75, viewBox = "0 0 24 24", children, ...rest }) => (
  <svg width={size} height={size} viewBox={viewBox} fill={fill} stroke={stroke} strokeWidth={strokeWidth}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" {...rest}>
    {d ? <path d={d} /> : children}
  </svg>
);
const IconGrid = (p) => <Icon {...p}><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></Icon>;
const IconHourglass = (p) => <Icon {...p} d="M6 2h12M6 22h12M6 2v4a6 6 0 0 0 12 0V2M6 22v-4a6 6 0 0 1 12 0v4" />;
const IconCheckCircle = (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M8 12.5 11 15.5 16 9.5" /></Icon>;
const IconXCircle = (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M9 9l6 6M15 9l-6 6" /></Icon>;
const IconCash = (p) => <Icon {...p}><rect x="3" y="6" width="18" height="12" rx="2" /><circle cx="12" cy="12" r="2.5" /><path d="M6 10v.01M18 14v.01" /></Icon>;
const IconSearch = (p) => <Icon {...p}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></Icon>;
const IconCalendarX = (p) => <Icon {...p}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4M10 14l4 4M14 14l-4 4" /></Icon>;
const IconChevronLeft = (p) => <Icon {...p} d="m15 18-6-6 6-6" />;
const IconChevronRight = (p) => <Icon {...p} d="m9 18 6-6-6-6" />;
const IconChevronDown = (p) => <Icon {...p} d="m6 9 6 6 6-6" />;
const IconMapPin = (p) => <Icon {...p}><path d="M12 22s-7-6.5-7-12a7 7 0 1 1 14 0c0 5.5-7 12-7 12Z" /><circle cx="12" cy="10" r="2.5" /></Icon>;
const IconUser = (p) => <Icon {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></Icon>;
const IconUserPlus = (p) => <Icon {...p}><circle cx="9" cy="8" r="4" /><path d="M2 21a7 7 0 0 1 13.5-2.5" /><path d="M19 8v6M16 11h6" /></Icon>;
const IconCalendar = (p) => <Icon {...p}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></Icon>;
const IconClock = (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>;
const IconMail = (p) => <Icon {...p}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 7 9-7" /></Icon>;
const IconEdit = (p) => <Icon {...p} d="M4 20h4l11-11-4-4L4 16v4ZM14 6l4 4" />;
const IconEye = (p) => <Icon {...p}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></Icon>;
const IconMoreVertical = (p) => (
  <Icon {...p} fill="currentColor" stroke="none">
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </Icon>
);
const IconAlert = (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16v.01" /></Icon>;
const IconDownload = (p) => <Icon {...p}><path d="M12 3v12M7 10l5 5 5-5M5 21h14" /></Icon>;

/* ── Status filters ── */
const STATUS_FILTERS = [
  { id: "all", label: "All", IconComp: IconGrid },
  { id: "pending", label: "Pending", IconComp: IconHourglass },
  { id: "confirmed", label: "Confirmed", IconComp: IconCheckCircle },
  { id: "declined", label: "Declined", IconComp: IconXCircle },
  { id: "paid", label: "Paid", IconComp: IconCash },
];

/* ── Date / time formatting ── */
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};
const fmtDateLong = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" });
};
const fmtTime = (hhmmss) => {
  if (!hhmmss) return "—";
  const [hStr, mStr] = String(hhmmss).split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr || "0", 10);
  if (Number.isNaN(h)) return hhmmss;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
};
const addHoursToTime = (start, hours) => {
  if (!start || hours == null) return null;
  const [h, m] = String(start).split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(h)) return null;
  const total = h + Number(hours);
  return `${String(total).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}`;
};
const sameDay = (a, b) => {
  if (!a || !b) return false;
  return new Date(a).toDateString() === new Date(b).toDateString();
};

/** Friendly relative date label: "Today" / "Tomorrow" / "In 3d" / "5d ago".
 *  Falls back to "Sat, Jun 6" for anything > 7 days away in either direction. */
const fmtDateSmart = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target - today) / (24 * 60 * 60 * 1000));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";
  if (diffDays > 0 && diffDays <= 7) return `In ${diffDays}d`;
  if (diffDays < 0 && diffDays >= -7) return `${Math.abs(diffDays)}d ago`;
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

/* ── Action dropdown menu (portal-based, escapes table overflow) ── */
const MENU_WIDTH = 220;

const ActionMenu = ({ items, label = "Booking actions" }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const popupRef = useRef(null);

  const computeCoords = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = r.right - MENU_WIDTH;
    if (left < 8) left = 8;
    if (left + MENU_WIDTH > vw - 8) left = vw - MENU_WIDTH - 8;
    let top = r.bottom + 6;
    const estHeight = Math.min(360, items.length * 40 + 16);
    if (top + estHeight > vh - 8) top = Math.max(8, r.top - estHeight - 6);
    setCoords({ top, left });
  }, [items.length]);

  useLayoutEffect(() => {
    if (open) computeCoords();
  }, [open, computeCoords]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (
        !buttonRef.current?.contains(e.target) &&
        !popupRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    const onReflow = () => computeCoords();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [open, computeCoords]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`mgr-menu__trigger ${open ? "mgr-menu__trigger--open" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
      >
        <IconMoreVertical size={18} />
      </button>
      {open && createPortal(
        <div
          ref={popupRef}
          role="menu"
          className="mgr-menu__popup"
          style={{ top: coords.top, left: coords.left, width: MENU_WIDTH }}
          aria-label={label}
        >
          {items.map((it, i) => {
            if (it.divider) return <div key={`d${i}`} className="mgr-menu__divider" role="separator" />;
            const Cmp = it.icon;
            return (
              <button
                key={it.key || i}
                type="button"
                role="menuitem"
                className={`mgr-menu__item ${it.variant === "danger" ? "mgr-menu__item--danger" : ""} ${it.variant === "accent" ? "mgr-menu__item--accent" : ""}`}
                onClick={() => {
                  setOpen(false);
                  it.onClick?.();
                }}
                disabled={it.disabled}
              >
                {Cmp && <Cmp size={15} />}
                <span>{it.label}</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
};

/* ── Per-day schedule panel (lazy-loaded) ── */
const SchedulePanel = ({ bookingId, token, loaderRef }) => {
  const [state, setState] = useState({ loading: true, error: null, rows: [] });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await getBookingSchedule(token, bookingId);
        if (cancelled) return;
        const rows = Array.isArray(res?.data?.dates) ? res.data.dates : [];
        setState({ loading: false, error: null, rows });
      } catch (err) {
        if (cancelled) return;
        setState({ loading: false, error: "Failed to load schedule", rows: [] });
      }
    };
    load();
    if (loaderRef) loaderRef.current = load;
    return () => {
      cancelled = true;
    };
  }, [bookingId, token, loaderRef]);

  if (state.loading) {
    return (
      <div className="mgr-schedule mgr-schedule--loading">
        <span className="mgr-spinner" /> Loading schedule…
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="mgr-schedule mgr-schedule--error">
        <IconAlert size={14} /> {state.error}
      </div>
    );
  }
  if (state.rows.length === 0) {
    return (
      <div className="mgr-schedule mgr-schedule--empty">
        <IconCalendarX size={16} /> No scheduled time slots for this booking.
      </div>
    );
  }

  const totalHours = state.rows.reduce((s, r) => s + Number(r.hours || 0), 0);

  return (
    <div className="mgr-schedule">
      <div className="mgr-schedule__head">
        <span className="mgr-schedule__title">
          <IconCalendar size={14} /> Schedule ({state.rows.length} session{state.rows.length > 1 ? "s" : ""})
        </span>
        <span className="mgr-schedule__total">
          <IconClock size={13} /> {totalHours} hr{totalHours > 1 ? "s" : ""} total
        </span>
      </div>
      <ul className="mgr-schedule__list">
        {state.rows.map((r, i) => {
          const end = addHoursToTime(r.start_time, r.hours);
          const accepted = r.acceptance === true;
          return (
            <li key={i} className="mgr-schedule__row">
              <span className="mgr-schedule__date">{fmtDateLong(r.date_day)}</span>
              <span className="mgr-schedule__time">
                <IconClock size={12} /> {fmtTime(r.start_time)} <span className="mgr-schedule__sep">→</span> {fmtTime(end)}
              </span>
              <span className="mgr-schedule__hours">{r.hours} hr{r.hours > 1 ? "s" : ""}</span>
              <span className={`mgr-schedule__chip ${accepted ? "mgr-schedule__chip--ok" : "mgr-schedule__chip--idle"}`}>
                {accepted ? "Accepted" : "Pending"}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const ManagerScreen = ({ globalSearch = "" }) => {
  const token = localStorage.getItem("@uphealth_login_token");

  const [allProviders, setAllProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allBookings, setAllBookings] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    pageSize: 15,
    totalItems: 0,
    totalPages: 1,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  const [providerModal, setProviderModal] = useState({ open: false, booking: null });
  const openProviderModal = (booking) => setProviderModal({ open: true, booking });
  const closeProviderModal = () => setProviderModal({ open: false, booking: null });

  const [detailBookingId, setDetailBookingId] = useState(null);
  const openDetailModal = (id) => setDetailBookingId(id);
  const closeDetailModal = () => setDetailBookingId(null);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDialog, setBulkDialog] = useState(null); // {action: 'confirm'|'decline'}
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkSummary, setBulkSummary] = useState(null); // {action, succeeded, failed}

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const [confirmDialog, setConfirmDialog] = useState(null); // {booking, action: 'confirm'|'decline'}
  const requestConfirm = (booking, action) => setConfirmDialog({ booking, action });
  const cancelConfirm = () => setConfirmDialog(null);
  const executeConfirm = () => {
    if (confirmDialog) {
      handleConfirmationSubmit(confirmDialog.booking.booking_id, confirmDialog.action === "confirm");
    }
    setConfirmDialog(null);
  };

  /* ── Data fetching ── */
  const fetchData = useCallback(
    async (page = 1, search = "") => {
      setLoading(true);
      setError(null);
      try {
        if (allProviders.length === 0) {
          const providersRes = await getAllProviders(token);
          setAllProviders(providersRes.data);
        }
        const bookingsRes = await getAdminBookings(token, page, paginationInfo.pageSize, search);

        if (bookingsRes.data && bookingsRes.data.pagination) {
          setAllBookings(bookingsRes.data.data);
          setPaginationInfo(bookingsRes.data.pagination);
        } else if (Array.isArray(bookingsRes.data)) {
          setAllBookings(bookingsRes.data);
          setPaginationInfo({ currentPage: 1, pageSize: 15, totalItems: bookingsRes.data.length, totalPages: 1 });
        } else {
          throw new Error("Unexpected data format from API.");
        }
      } catch (err) {
        setError("Failed to fetch data. Please try again.");
        console.error(err);
      }
      setLoading(false);
    },
    [token, allProviders.length, paginationInfo.pageSize]
  );

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const effectiveSearch = debouncedSearchTerm || globalSearch;
    fetchData(1, effectiveSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, debouncedSearchTerm, globalSearch]);

  /* ── Actions ── */
  const handleProviderChange = async (booking_id, new_provider_id) => {
    if (!new_provider_id) return;
    try {
      await updateBookingProvider(token, booking_id, new_provider_id);
      fetchData(paginationInfo.currentPage, debouncedSearchTerm || globalSearch);
    } catch (err) {
      alert("Failed to update provider.");
    }
  };

  const handleConfirmationSubmit = async (booking_id, acceptanceStatus) => {
    try {
      await updateBookingConfirmation(token, booking_id, acceptanceStatus);
      fetchData(paginationInfo.currentPage, debouncedSearchTerm || globalSearch);
    } catch (err) {
      alert(`Failed to ${acceptanceStatus ? "confirm" : "decline"} booking.`);
    }
  };

  /* ── Bulk actions ── */
  const executeBulk = async () => {
    if (!bulkDialog || selectedIds.size === 0) {
      setBulkDialog(null);
      return;
    }
    const action = bulkDialog.action;
    const ids = Array.from(selectedIds);
    setBulkDialog(null);
    setBulkBusy(true);
    try {
      const res = await bulkBookingAction(token, ids, action);
      const { succeeded = 0, failed = 0 } = res.data || {};
      setBulkSummary({ action, succeeded, failed });
      clearSelection();
      await fetchData(paginationInfo.currentPage, debouncedSearchTerm || globalSearch);
      // Auto-dismiss summary after 4s
      setTimeout(() => setBulkSummary(null), 4000);
    } catch (err) {
      console.error("Bulk action failed:", err);
      alert(`Bulk ${action} failed. ${err?.response?.data || ""}`);
    } finally {
      setBulkBusy(false);
    }
  };

  /* CSV export of selected rows (client-side, no backend round-trip). */
  const exportSelectedCsv = () => {
    if (selectedIds.size === 0) return;
    const rows = allBookings.filter((b) => selectedIds.has(b.booking_id));
    const headers = [
      "booking_id", "patient_name", "service", "start_date", "end_date",
      "total_hours", "provider", "city", "country", "status", "paid",
    ];
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(",")];
    rows.forEach((b) => {
      lines.push([
        b.booking_id,
        `${b.first_name || ""} ${b.last_name || ""}`.trim(),
        b.care_service_name || "",
        b.start_date || "",
        b.end_date || "",
        b.total_hours ?? "",
        b.provider_name || "",
        b.city || "",
        b.country || "",
        getStatus(b).label,
        b.paid ? "yes" : "no",
      ].map(escape).join(","));
    });
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    link.href = url;
    link.download = `bookings-${rows.length}-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /* ── Derived state ── */
  const getStatus = (b) => {
    if (b.paid) return { id: "paid", label: "Paid", cls: "dt-pill--accent" };
    if (b.acceptance === true) return { id: "confirmed", label: "Confirmed", cls: "dt-pill--success" };
    if (b.acceptance === false) return { id: "declined", label: "Declined", cls: "dt-pill--neutral" };
    return { id: "pending", label: "Pending", cls: "dt-pill--warn" };
  };

  const counts = useMemo(() => {
    const c = { all: allBookings.length, pending: 0, confirmed: 0, declined: 0, paid: 0 };
    allBookings.forEach((b) => { c[getStatus(b).id]++; });
    return c;
  }, [allBookings]);

  const filteredBookings = useMemo(() => {
    if (filter === "all") return allBookings;
    return allBookings.filter((b) => getStatus(b).id === filter);
  }, [allBookings, filter]);

  /* ── Pagination ── */
  const handleNextPage = () => {
    if (paginationInfo.currentPage < paginationInfo.totalPages) {
      fetchData(paginationInfo.currentPage + 1, debouncedSearchTerm || globalSearch);
    }
  };
  const handlePreviousPage = () => {
    if (paginationInfo.currentPage > 1) {
      fetchData(paginationInfo.currentPage - 1, debouncedSearchTerm || globalSearch);
    }
  };

  if (error) {
    return <div className="alert alert-danger" role="alert">{error}</div>;
  }

  return (
    <div className="manager-container">
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 className="page-header__title">Bookings</h1>
          <div className="page-header__subtitle">
            Showing <strong style={{ color: "var(--text-1)" }}>{filteredBookings.length}</strong> on this page · <strong style={{ color: "var(--text-1)" }}>{paginationInfo.totalItems}</strong> total bookings
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="dt-toolbar">
        <div className="dt-toolbar__left">
          <div className="dt-filters">
            {STATUS_FILTERS.map((f) => {
              const Cmp = f.IconComp;
              const active = filter === f.id;
              return (
                <button
                  key={f.id}
                  className={`dt-filter-pill ${active ? "dt-filter-pill--active" : ""}`}
                  onClick={() => setFilter(f.id)}
                  aria-pressed={active}
                >
                  <Cmp size={14} />
                  {f.label}
                  <span className="dt-filter-pill__count">{counts[f.id] || 0}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="dt-toolbar__right">
          <div className="dt-search">
            <span className="dt-search__icon"><IconSearch size={16} /></span>
            <input
              type="text"
              placeholder="Search by name, service, location…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search bookings"
            />
          </div>
        </div>
      </div>

      {/* Bulk action bar (only when selection > 0) */}
      {selectedIds.size > 0 && (
        <div className="mgr-bulkbar" role="region" aria-label="Bulk actions">
          <div className="mgr-bulkbar__left">
            <span className="mgr-bulkbar__count">{selectedIds.size}</span>
            <span className="mgr-bulkbar__label">selected</span>
            <button type="button" className="mgr-bulkbar__clear" onClick={clearSelection}>
              Clear
            </button>
          </div>
          <div className="mgr-bulkbar__actions">
            <button
              type="button"
              className="mgr-bulkbar__btn mgr-bulkbar__btn--accent"
              onClick={() => setBulkDialog({ action: "confirm" })}
              disabled={bulkBusy}
              title="Confirm all selected bookings"
            >
              <IconCheckCircle size={14} /> Confirm all
            </button>
            <button
              type="button"
              className="mgr-bulkbar__btn mgr-bulkbar__btn--danger"
              onClick={() => setBulkDialog({ action: "decline" })}
              disabled={bulkBusy}
              title="Decline all selected bookings"
            >
              <IconXCircle size={14} /> Decline all
            </button>
            <button
              type="button"
              className="mgr-bulkbar__btn"
              onClick={exportSelectedCsv}
              disabled={bulkBusy}
              title="Download selected rows as CSV"
            >
              <IconDownload size={14} /> Export CSV
            </button>
          </div>
        </div>
      )}

      {/* Bulk action summary toast (auto-dismisses) */}
      {bulkSummary && (
        <div
          className={`mgr-bulktoast ${bulkSummary.failed > 0 ? "mgr-bulktoast--mixed" : "mgr-bulktoast--ok"}`}
          role="status"
          aria-live="polite"
        >
          {bulkSummary.failed === 0 ? (
            <><IconCheckCircle size={14} /> {bulkSummary.action === "confirm" ? "Confirmed" : "Declined"} {bulkSummary.succeeded} booking{bulkSummary.succeeded !== 1 ? "s" : ""}.</>
          ) : (
            <><IconAlert size={14} /> {bulkSummary.action === "confirm" ? "Confirmed" : "Declined"} {bulkSummary.succeeded} · {bulkSummary.failed} failed.</>
          )}
          <button type="button" className="mgr-bulktoast__close" onClick={() => setBulkSummary(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      {/* Data table */}
      <div className="dt-shell">
        {loading ? (
          <div className="dt-loading">
            <span className="mgr-spinner mgr-spinner--lg" />
            <span>Loading bookings…</span>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="dt-empty">
            <div className="dt-empty__icon"><IconCalendarX size={28} /></div>
            <h4>No bookings found</h4>
            <p>{filter !== "all" ? "Try a different filter." : "Try a different search term."}</p>
          </div>
        ) : (
          <table className="dt-table dt-table--cards">
            <thead>
              <tr>
                <th className="mgr-col-select" aria-label="Select">
                  {(() => {
                    const pageIds = filteredBookings.map((b) => b.booking_id);
                    const selectedOnPage = pageIds.filter((id) => selectedIds.has(id)).length;
                    const allSelected = selectedOnPage > 0 && selectedOnPage === pageIds.length;
                    const someSelected = selectedOnPage > 0 && !allSelected;
                    const onToggleAll = () => {
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (allSelected) {
                          pageIds.forEach((id) => next.delete(id));
                        } else {
                          pageIds.forEach((id) => next.add(id));
                        }
                        return next;
                      });
                    };
                    return (
                      <input
                        type="checkbox"
                        className="mgr-check"
                        aria-label={allSelected ? "Deselect all rows on this page" : "Select all rows on this page"}
                        checked={allSelected}
                        ref={(el) => { if (el) el.indeterminate = someSelected; }}
                        onChange={onToggleAll}
                      />
                    );
                  })()}
                </th>
                <th style={{ width: 40 }} aria-label="Expand" />
                <th>Patient</th>
                <th>Booking</th>
                <th>Provider</th>
                <th>Status</th>
                <th className="dt-th--right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings.map((b) => {
                const status = getStatus(b);
                const initials = `${(b.first_name?.[0] || "")}${(b.last_name?.[0] || "")}`.toUpperCase() || "U";
                const nearbyProviders = allProviders.filter(
                  (p) =>
                    p.verification === true &&
                    p.city?.toLowerCase() === b.city?.toLowerCase() &&
                    p.country?.toLowerCase() === b.country?.toLowerCase()
                );
                const current = nearbyProviders.find((p) => p.provider_id === b.provider_id);
                const isExpanded = expandedId === b.booking_id;
                const oneDay = sameDay(b.start_date, b.end_date);
                const totalH = b.total_hours;

                const menuItems = [
                  ...(status.id === "pending"
                    ? [
                        { key: "confirm", label: "Confirm booking", icon: IconCheckCircle, variant: "accent",
                          onClick: () => requestConfirm(b, "confirm") },
                        { key: "decline", label: "Decline booking", icon: IconXCircle, variant: "danger",
                          onClick: () => requestConfirm(b, "decline") },
                        { divider: true },
                      ]
                    : []),
                  { key: "contact", label: "Contact patient", icon: IconMail,
                    onClick: () => alert("Contact patient — coming soon") },
                  { key: "edit", label: "Edit booking", icon: IconEdit,
                    onClick: () => alert("Edit booking — coming soon") },
                  { key: "view", label: "View details", icon: IconEye,
                    onClick: () => openDetailModal(b.booking_id) },
                ];

                // Clicks on interactive descendants (buttons, links, menus)
                // should NOT open the detail modal. Only "background" clicks
                // on the row's cell area open the modal.
                const handleRowClick = (e) => {
                  if (e.target.closest('button, a, [role="menuitem"], [role="menu"], input')) return;
                  openDetailModal(b.booking_id);
                };
                const handleRowKey = (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    if (e.target !== e.currentTarget) return;
                    e.preventDefault();
                    openDetailModal(b.booking_id);
                  }
                };

                const isSelected = selectedIds.has(b.booking_id);

                return (
                  <React.Fragment key={b.booking_id}>
                    <tr
                      className={`mgr-row-clickable mgr-row--stripe-${status.id} ${isExpanded ? "dt-row--expanded" : ""} ${isSelected ? "mgr-row--selected" : ""}`}
                      onClick={handleRowClick}
                      onKeyDown={handleRowKey}
                      tabIndex={0}
                      role="row"
                      aria-label={`View booking ${String(b.booking_id).slice(-8)} for ${b.first_name} ${b.last_name}`}
                    >
                      {/* Select checkbox */}
                      <td className="mgr-col-select">
                        <input
                          type="checkbox"
                          className="mgr-check"
                          checked={isSelected}
                          onChange={() => toggleSelect(b.booking_id)}
                          aria-label={`Select booking ${String(b.booking_id).slice(-8)}`}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      {/* Expand toggle */}
                      <td className="dt-td--icon">
                        <button
                          type="button"
                          className={`mgr-expand-btn ${isExpanded ? "mgr-expand-btn--open" : ""}`}
                          aria-expanded={isExpanded}
                          aria-controls={`mgr-detail-${b.booking_id}`}
                          aria-label={isExpanded ? "Hide schedule" : "Show schedule"}
                          onClick={() => setExpandedId(isExpanded ? null : b.booking_id)}
                        >
                          <IconChevronDown size={16} />
                        </button>
                      </td>

                      {/* Patient */}
                      <td>
                        <div className="dt-user">
                          <div className="dt-avatar">{initials}</div>
                          <div className="dt-cell-stack">
                            <div className="dt-name">{b.first_name} {b.last_name}</div>
                            <div className="dt-mono" title={`Booking #${b.booking_id}`}>
                              #{b.booking_id}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Booking — merged Service + Schedule + Location */}
                      <td className="mgr-booking-cell">
                        <div className="mgr-booking">
                          <div className="mgr-booking__service">{b.care_service_name || "—"}</div>
                          <div className="mgr-booking__meta">
                            <span className="mgr-booking__when" title={oneDay ? fmtDate(b.start_date) : `${fmtDate(b.start_date)} → ${fmtDate(b.end_date)}`}>
                              <IconCalendar size={11} />
                              {oneDay
                                ? fmtDateSmart(b.start_date)
                                : `${fmtDateSmart(b.start_date)} → ${fmtDateSmart(b.end_date)}`}
                            </span>
                            <span className="mgr-booking__sep">·</span>
                            <span className="mgr-booking__hours">
                              <IconClock size={11} />
                              {totalH != null ? `${totalH} hr${totalH !== 1 ? "s" : ""}` : "—"}
                            </span>
                            <span className="mgr-booking__sep">·</span>
                            <span className="mgr-booking__loc" title={[b.city, b.country].filter(Boolean).join(", ")}>
                              <IconMapPin size={11} />
                              {b.city}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Provider */}
                      <td>
                        {nearbyProviders.length > 0 ? (
                          <button
                            type="button"
                            className={`provider-trigger ${current ? "provider-trigger--assigned" : "provider-trigger--empty"}`}
                            onClick={() =>
                              openProviderModal({
                                booking_id: b.booking_id,
                                provider_id: b.provider_id,
                                providers: nearbyProviders,
                                label: `#${String(b.booking_id).slice(-8)}`,
                              })
                            }
                            title={current ? "Change provider" : "Assign provider"}
                          >
                            {current ? (
                              <>
                                <span className="provider-trigger__avatar">
                                  {current.img_url ? <img src={current.img_url} alt="" /> : <IconUser size={14} />}
                                </span>
                                <span className="provider-trigger__text">
                                  <span className="provider-trigger__name">{current.provider_name}</span>
                                  <span className="provider-trigger__sub">{current.title || "Tap to change"}</span>
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="provider-trigger__avatar provider-trigger__avatar--empty"><IconUserPlus size={14} /></span>
                                <span className="provider-trigger__text">
                                  <span className="provider-trigger__name">Assign provider</span>
                                  <span className="provider-trigger__sub">{nearbyProviders.length} nearby</span>
                                </span>
                              </>
                            )}
                            <span className="provider-trigger__chev"><IconChevronRight size={14} /></span>
                          </button>
                        ) : (
                          <span className="dt-pill dt-pill--neutral">No nearby</span>
                        )}
                      </td>

                      {/* Status + inline primary actions when pending */}
                      <td>
                        <div className="mgr-status-cell">
                          <span className={`dt-pill ${status.cls}`}>{status.label}</span>
                          {status.id === "pending" && (
                            <div className="mgr-inline-actions" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className="mgr-inline-btn mgr-inline-btn--accent"
                                onClick={() => requestConfirm(b, "confirm")}
                                title="Confirm booking"
                                aria-label={`Confirm booking ${String(b.booking_id).slice(-8)}`}
                              >
                                <IconCheckCircle size={14} />
                              </button>
                              <button
                                type="button"
                                className="mgr-inline-btn mgr-inline-btn--danger"
                                onClick={() => requestConfirm(b, "decline")}
                                title="Decline booking"
                                aria-label={`Decline booking ${String(b.booking_id).slice(-8)}`}
                              >
                                <IconXCircle size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="dt-td--right">
                        <ActionMenu items={menuItems} label={`Actions for booking ${String(b.booking_id).slice(-8)}`} />
                      </td>
                    </tr>

                    {/* Expanded schedule panel — leave the sticky Actions
                        column empty so the panel doesn't bleed behind it.
                        Cols: select + expand + 4 middle (Patient/Booking/Provider/Status) + sticky right. */}
                    {isExpanded && (
                      <tr id={`mgr-detail-${b.booking_id}`} className="dt-row__detail">
                        <td />
                        <td />
                        <td colSpan={4}>
                          <SchedulePanel bookingId={b.booking_id} token={token} />
                        </td>
                        <td className="dt-td--right" aria-hidden="true" />
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {!loading && filteredBookings.length > 0 && (
          <div className="dt-footer">
            <div>
              Page <span className="dt-pager-info">{paginationInfo.currentPage}</span> of {paginationInfo.totalPages}
              <span style={{ margin: "0 10px", color: "var(--border-3)" }}>·</span>
              <span className="dt-pager-info">{paginationInfo.totalItems}</span> total
            </div>
            <div className="dt-footer__pager">
              <button
                className="dt-pager-btn"
                onClick={handlePreviousPage}
                disabled={paginationInfo.currentPage <= 1 || loading}
                aria-label="Previous page"
              >
                <IconChevronLeft size={16} />
              </button>
              <button
                className="dt-pager-btn"
                onClick={handleNextPage}
                disabled={paginationInfo.currentPage >= paginationInfo.totalPages || loading}
                aria-label="Next page"
              >
                <IconChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      <ProviderModal
        show={providerModal.open}
        onClose={closeProviderModal}
        providers={providerModal.booking?.providers || []}
        selectedProviderId={providerModal.booking?.provider_id}
        bookingId={providerModal.booking?.booking_id}
        bookingLabel={providerModal.booking?.label}
        onSelect={(newProviderId) => handleProviderChange(providerModal.booking?.booking_id, newProviderId)}
        onFetchDetail={(providerId) => getProviderDetail(token, providerId)}
        onFetchSchedule={(bookingId) => getBookingSchedule(token, bookingId)}
        onFetchAvailabilityBatch={(providerIds, start, end) =>
          getProvidersAvailabilityBatch(token, providerIds, start, end)
        }
      />

      <ConfirmModal
        show={!!confirmDialog}
        onConfirm={executeConfirm}
        onCancel={cancelConfirm}
        title={confirmDialog?.action === "decline" ? "Decline booking?" : "Confirm booking?"}
        message={
          confirmDialog
            ? `${confirmDialog.action === "decline" ? "Decline" : "Confirm"} booking for ${confirmDialog.booking.first_name} ${confirmDialog.booking.last_name}?`
            : undefined
        }
        confirmLabel={confirmDialog?.action === "decline" ? "Decline" : "Confirm"}
        variant={confirmDialog?.action === "decline" ? "danger" : "accent"}
      />

      <BookingDetailModal
        show={!!detailBookingId}
        bookingId={detailBookingId}
        onClose={closeDetailModal}
        onFetchDetail={(id) => getBookingDetail(token, id)}
      />

      <ConfirmModal
        show={!!bulkDialog}
        onConfirm={executeBulk}
        onCancel={() => setBulkDialog(null)}
        title={
          bulkDialog?.action === "decline"
            ? `Decline ${selectedIds.size} booking${selectedIds.size !== 1 ? "s" : ""}?`
            : `Confirm ${selectedIds.size} booking${selectedIds.size !== 1 ? "s" : ""}?`
        }
        message={
          bulkDialog
            ? `${bulkDialog.action === "decline" ? "Decline" : "Confirm"} all ${selectedIds.size} selected booking${selectedIds.size !== 1 ? "s" : ""}? Bookings without an assigned provider will be skipped.`
            : undefined
        }
        confirmLabel={bulkDialog?.action === "decline" ? `Decline ${selectedIds.size}` : `Confirm ${selectedIds.size}`}
        variant={bulkDialog?.action === "decline" ? "danger" : "accent"}
      />
    </div>
  );
};

export default ManagerScreen;
